import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { getRobotById } from '@/api/robots.api'
import type { Robot } from '@/types/api'
import { getStatusBadgeClass } from './columns'

interface RobotDetailDrawerProps {
  robotId: string | null
  showUnitColumn?: boolean
  onClose: () => void
  onUpdate?: (robot: Robot) => void
}

export function RobotDetailDrawer({
  robotId,
  showUnitColumn,
  onClose,
  onUpdate,
}: RobotDetailDrawerProps) {
  const { data: robot, isLoading } = useQuery({
    queryKey: ['robot-detail', robotId],
    queryFn: ({ signal }) =>
      robotId ? getRobotById(robotId, signal) : Promise.resolve(null),
    enabled: !!robotId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const renderState = (state?: string) => {
    if (state === undefined) return '-'

    return (
      <span
        className={`inline-flex rounded-full px-4 py-1.5 text-xs font-semibold ${getStatusBadgeClass(
          state,
        )}`}
      >
        {state}
      </span>
    )
  }

  return (
    <Drawer
      open={!!robotId}
      onClose={onClose}
      title="CHI TIẾT ROBOT"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter className="flex justify-end gap-3 w-full">
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          {robot?.state !== 'Đã xóa' && (
            <Button
              variant="primary"
              size="md"
              className="min-w-[160px] h-10"
              onClick={() => robot && onUpdate?.(robot)}
            >
              Cập nhật
            </Button>
          )}
        </DrawerFooter>
      }
    >
      {isLoading && (
        <div className="p-6 text-center text-gray-500">
          Đang tải chi tiết...
        </div>
      )}

      {robot && (
        <div className="space-y-6">
          <DrawerSection title="">
            <DrawerDetailRow label="Tên robot:" value={robot.name} />
            {showUnitColumn && (
              <DrawerDetailRow
                label="Đơn vị:"
                value={robot.unitResponse?.name || robot.unitId || '-'}
              />
            )}
            <DrawerDetailRow
              label="Sơ đồ:"
              value={robot.diagramInfo?.name || '-'}
            />
            <DrawerDetailRow
              label="Hiện trạng:"
              value={renderState(robot.state)}
            />
          </DrawerSection>
        </div>
      )}
    </Drawer>
  )
}
